/**
 * Shared gate + lookup for applying translation overrides to content that is
 * rendered from hard-coded per-locale tables (docs + learn) rather than through
 * next-intl `t()`.
 *
 * next-intl's request pipeline (`i18n/request.ts`) layers DB overrides onto the
 * message bundle, so `t()`-rendered copy goes live automatically. Docs/learn
 * copy bypasses that pipeline, so these helpers let the pages overlay the same
 * overrides by key at render time. Both are gated on the SAME flag request.ts
 * uses (`NEXT_PUBLIC_I18N_OVERRIDES_ENABLED`), so production stays table-only and
 * unvetted DB copy never ships.
 */

export function contentOverridesEnabled(): boolean {
  return process.env.NEXT_PUBLIC_I18N_OVERRIDES_ENABLED === '1'
}

/**
 * Returns the override for `key` when present and non-empty, else `fallback`.
 * Empty-string overrides are skipped so the underlying table value wins —
 * mirroring `applyOverrides`/`mergeWithSourceFallback` semantics.
 */
export function pickOverride(
  overrides: Record<string, string> | null | undefined,
  key: string,
  fallback: string,
): string {
  const value = overrides?.[key]
  return value !== undefined && value !== '' ? value : fallback
}

export function hasOverrides(
  overrides: Record<string, string> | null | undefined,
): overrides is Record<string, string> {
  return !!overrides && Object.keys(overrides).length > 0
}
